import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AlertTriangle, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/useWorkspace";
import { tierAiBundle, type Tier } from "@/lib/tier";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type CreditStats = {
  tier: Tier;
  monthly_credits_cap: number | null;
  monthly_credits_used: number;
  topup_credits_balance: number;
  byok_active: boolean;
};

type BannerState = "healthy" | "headsup" | "warning" | "exhausted";

/** Routes where the banner should never appear. */
const HIDDEN_PATH_PREFIXES = [
  "/auth",
  "/login",
  "/signup",
  "/pricing",
  "/legal",
  "/privacy",
  "/terms",
  "/share",          // public excerpt share pages
  "/checkout-return",
];

const computeState = (data: CreditStats): BannerState => {
  const cap = data.monthly_credits_cap;
  const used = data.monthly_credits_used;
  const topup = data.topup_credits_balance;
  const isByokTier = data.tier === "elite" || data.tier === "agency";

  if (isByokTier) {
    // BYOK tiers: only nudge if topup is genuinely low and they have no key.
    return !data.byok_active && topup < 25 ? "warning" : "healthy";
  }
  if (cap === null || cap <= 0) return "healthy";

  const pct = (used / cap) * 100;
  if (pct >= 100 && topup < 25) return "exhausted";
  if (pct >= 90) return "warning";
  if (pct >= 75) return "headsup";
  return "healthy";
};

const STATE_STYLES: Record<Exclude<BannerState, "healthy">, string> = {
  headsup:
    "border-border bg-muted/40 text-foreground",
  warning:
    "border-amber-500/40 bg-amber-500/10 text-foreground dark:border-amber-400/30",
  exhausted:
    "border-destructive/40 bg-destructive/10 text-foreground",
};

const STATE_COPY: Record<Exclude<BannerState, "healthy">, string> = {
  headsup: "Heads up: you've used most of this month's AI credits.",
  warning: "Almost out of AI credits this month.",
  exhausted: "You're out of monthly AI credits.",
};

export const LowCreditsBanner = () => {
  const { active } = useWorkspace();
  const location = useLocation();
  const [dismissed, setDismissed] = useState<string | null>(null);

  const tier = active?.account_tier ?? null;
  const bundle = tierAiBundle(tier);

  // Only fetch when we have a workspace AND its tier uses AI at all.
  const enabled = !!active?.id && bundle !== "none";

  const q = useQuery({
    queryKey: ["account-ai-usage", active?.id],
    queryFn: async (): Promise<CreditStats> => {
      const { data, error } = await supabase.functions.invoke("account-ai-usage", {
        body: { workspace_id: active!.id },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      return data as CreditStats;
    },
    enabled,
    staleTime: 60_000,
  });

  const state: BannerState = useMemo(
    () => (q.data ? computeState(q.data) : "healthy"),
    [q.data],
  );

  const dismissKey = active?.id ? `lowCreditsBanner:${active.id}:${state}` : null;

  useEffect(() => {
    if (!dismissKey) return;
    setDismissed(sessionStorage.getItem(dismissKey));
  }, [dismissKey]);

  if (!enabled || state === "healthy") return null;
  if (HIDDEN_PATH_PREFIXES.some((p) => location.pathname.startsWith(p))) return null;
  if (dismissed === "1") return null;

  const handleDismiss = () => {
    if (dismissKey) sessionStorage.setItem(dismissKey, "1");
    setDismissed("1");
  };

  return (
    <div
      role="status"
      className={cn(
        "mx-auto mt-2 mb-1 flex max-w-6xl items-center gap-3 rounded-md border px-3 py-2 text-sm",
        STATE_STYLES[state],
      )}
    >
      <AlertTriangle
        className={cn(
          "h-4 w-4 shrink-0",
          state === "exhausted" ? "text-destructive" : "text-amber-600 dark:text-amber-400",
        )}
        aria-hidden
      />
      <div className="flex-1">
        <span className="font-medium">{STATE_COPY[state]}</span>
        {q.data && q.data.monthly_credits_cap !== null && (
          <span className="ml-1 text-xs text-muted-foreground">
            {q.data.monthly_credits_used.toLocaleString()} /{" "}
            {q.data.monthly_credits_cap.toLocaleString()} used
            {q.data.topup_credits_balance > 0 && (
              <> · {q.data.topup_credits_balance.toLocaleString()} top-up credits available</>
            )}
          </span>
        )}
      </div>
      <Button asChild size="sm" variant={state === "exhausted" ? "destructive" : "default"}>
        <Link to="/settings?buy_credits=1">Buy credits</Link>
      </Button>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss"
        className="rounded p-1 text-muted-foreground transition hover:bg-background/40 hover:text-foreground"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};
